import { useState } from "react";
import { mixRouteModesAudioStream } from "@services/ai/aiApi";
import { resolveMediaKind, resolveMediaUrl } from "@utils/media/mediaUrl";
import {
  normalizeMixedAudioByLanguage,
  normalizeRouteMusicByLanguage,
} from "@utils/mode_tool/modeRoute";

function getVoiceAudioForLanguage(point = {}, languageIso = "") {
  const language = String(languageIso || "").toLowerCase().trim();
  const multimediaAudio = (Array.isArray(point?.multimedia) ? point.multimedia : [])
    .filter((item) => resolveMediaKind(item) === "audio");
  const audios = Array.isArray(point?.audios) ? point.audios : [];
  const candidates = [...multimediaAudio, ...audios];
  return candidates.find((item) =>
    String(item?.language || item?.languageIso || "").toLowerCase() === language,
  ) || null;
}

function useRouteAudioMixing({ tourId, onCommitPoint }) {
  const [mixingTarget, setMixingTarget] = useState({ pointId: "", language: "" });
  const [errorByPointLanguage, setErrorByPointLanguage] = useState({});

  async function mixPoint(point = {}, languageIso = "en") {
    const pointId = String(point?.id || "");
    const language = String(languageIso || "").toLowerCase().trim();
    const requestResultKey = `${pointId}:${language}`;
    if (!pointId || !language || mixingTarget.pointId) return null;
    const voiceAudio = getVoiceAudioForLanguage(point, language);
    const multimediaAudio = (Array.isArray(point?.multimedia) ? point.multimedia : [])
      .filter((item) => resolveMediaKind(item) === "audio");
    const voiceAudioId = String(voiceAudio?.id || voiceAudio?.sourceId || "");
    const musicAudio = normalizeRouteMusicByLanguage(point?.music)
      .find((item) => item.language === language);
    const musicAudioId = String(musicAudio?.id || "");

    if (!String(tourId || "").trim()) {
      setErrorByPointLanguage((current) => ({ ...current, [requestResultKey]: "The tour ID is unavailable." }));
      return null;
    }
    if (!voiceAudioId) {
      setErrorByPointLanguage((current) => ({ ...current, [requestResultKey]: `This point needs a ${language.toUpperCase()} voice audio before mixing.` }));
      return null;
    }
    if (!musicAudioId) {
      setErrorByPointLanguage((current) => ({ ...current, [requestResultKey]: "Generate music for this point before mixing." }));
      return null;
    }

    setMixingTarget({ pointId, language });
    setErrorByPointLanguage((current) => ({ ...current, [requestResultKey]: "" }));
    const pendingPoint = {
      ...point,
      mixed_audio: normalizeMixedAudioByLanguage(point?.mixed_audio, [
        ...(Array.isArray(point?.audios) ? point.audios : []),
        ...multimediaAudio,
      ]).filter((item) => item.language !== language),
    };
    onCommitPoint?.(pointId, pendingPoint);
    let updatedPoint = null;
    let resultError = "";

    const applyMixResult = (eventData) => {
      const row = eventData?.point ?? eventData?.data ?? eventData;
      const status = String(eventData?.status || (row?.error ? "error" : "completed"));
      if (String(eventData?.id || row?.id || "") !== pointId) return;
      const resultLanguage = String(
        eventData?.language || row?.language || language,
      ).toLowerCase().trim();
      const resultKey = `${pointId}:${resultLanguage}`;
      if (status === "error" || row?.error || !row?.mixed_audio?.id || !row?.mixed_audio?.url) {
        resultError = String(eventData?.message || row?.message || "Audio mixing failed.");
        setErrorByPointLanguage((current) => ({
          ...current,
          [resultKey]: resultError,
        }));
        return;
      }

      const mixedAudio = {
        ...row.mixed_audio,
        id: String(row.mixed_audio.id),
        url: resolveMediaUrl(String(row.mixed_audio.url)),
        mime_type: String(row.mixed_audio.mime_type || "audio/mpeg"),
        duration_seconds: Number(row.mixed_audio.duration_seconds || 0),
        source_voice_audio_id: voiceAudioId,
        language: resultLanguage,
      };
      const nextAudios = (Array.isArray(point?.audios) ? point.audios : [])
        .filter((audio) => String(audio?.language || audio?.languageIso || "").toLowerCase() !== resultLanguage);
      nextAudios.push({
        ...voiceAudio,
        id: mixedAudio.id,
        url: mixedAudio.url,
        language: resultLanguage,
      });
      const mixedAudioList = normalizeMixedAudioByLanguage(point?.mixed_audio, [
        ...(Array.isArray(point?.audios) ? point.audios : []),
        ...multimediaAudio,
      ]);
      const uniqueMixedAudios = new Map();
      mixedAudioList
        .filter((item) => {
          const itemLanguage = String(item?.language || item?.languageIso || "")
            .toLowerCase()
            .trim();
          const itemId = String(item?.id || "");
          const itemSourceVoiceId = String(item?.source_voice_audio_id || "");
          return itemLanguage !== resultLanguage &&
            itemId !== voiceAudioId &&
            itemSourceVoiceId !== voiceAudioId &&
            itemId !== mixedAudio.id;
        })
        .concat(mixedAudio)
        .forEach((item) => {
          const itemLanguage = String(item?.language || item?.languageIso || "")
            .toLowerCase()
            .trim();
          if (!itemLanguage || !item?.id) return;
          uniqueMixedAudios.set(itemLanguage, item);
        });
      updatedPoint = {
        ...point,
        mixed_audio: Array.from(uniqueMixedAudios.values()),
        audios: nextAudios,
      };
      onCommitPoint?.(pointId, updatedPoint);
      setErrorByPointLanguage((current) => ({ ...current, [resultKey]: "" }));
    };

    try {
      const response = await mixRouteModesAudioStream({
        tour_id: String(tourId),
        points: [{
          id: pointId,
          language,
          voice_audio_id: voiceAudioId,
          music_audio_id: musicAudioId,
        }],
      }, ({ event, data }) => {
        if (event === "point") applyMixResult(data);
      });
      const resultPoints = response?.body?.points;
      if (!updatedPoint && Array.isArray(resultPoints)) {
        resultPoints.forEach((row) => applyMixResult({
          id: row?.id,
          point: row,
          status: row?.error ? "error" : "completed",
          message: row?.message,
        }));
      }
      if (!updatedPoint && !resultError) {
        resultError = "The stream completed without a mixed audio result.";
        setErrorByPointLanguage((current) => ({ ...current, [requestResultKey]: resultError }));
      }
      return updatedPoint;
    } catch (mixError) {
      setErrorByPointLanguage((current) => ({
        ...current,
        [requestResultKey]: mixError?.message || "Audio mixing failed.",
      }));
      return null;
    } finally {
      setMixingTarget({ pointId: "", language: "" });
    }
  }

  return {
    mixingPointId: mixingTarget.pointId,
    mixingLanguageIso: mixingTarget.language,
    errorByPointLanguage,
    mixPoint,
  };
}

export default useRouteAudioMixing;
